import React, { useState, useEffect, useCallback } from "react";
import Layout from "@/components/Layout";
import { getSettledMarkets, getSettledBets, voidMarketAfterSettlement, voidBetAfterSettlement } from "@/services/api";
import * as Dialog from "@radix-ui/react-dialog";

const LIMIT = 200;

const MARKETS_FOR_FANCY = [
  "1st Innings 6 Overs Line",
  "2nd Innings 6 Overs Line",
  "3rd Innings 6 Overs Line",
  "1st Innings 50 Overs Line",
  "2nd Innings 50 Overs Line",
  "3rd Innings 50 Overs Line",
  "1st Innings 40 Overs Line",
  "2nd Innings 40 Overs Line",
  "3rd Innings 40 Overs Line",
  "1st Innings 30 Overs Line",
  "2nd Innings 30 Overs Line",
  "3rd Innings 30 Overs Line",
  "1st Innings 20 Overs Line",
  "2nd Innings 20 Overs Line",
  "3rd Innings 20 Overs Line",
  "1st Innings 10 Overs Line",
  "2nd Innings 10 Overs Line",
  "3rd Innings 10 Overs Line",
  "Over By Over",
  "Ball By Ball",
  "Normal",
  "khado",
  "meter",
  "fancy1",
  "oddeven",
];

const TABS = [
  { key: "MATCH_ODDS", label: "Match Odds" },
  { key: "BOOKMAKER", label: "Bookmaker" },
  { key: "FANCY", label: "Fancy" },
  { key: "OTHERS", label: "Others" },
];

function getTabForMarket(mkt) {
  const mt = mkt.marketType || "";
  if (mt === "MATCH_ODDS") return "MATCH_ODDS";
  if (mt === "BOOKMAKER") return "BOOKMAKER";
  if (MARKETS_FOR_FANCY.includes(mt)) return "FANCY";
  return "OTHERS";
}

export default function VoidBets() {
  const [activeTab, setActiveTab] = useState("MATCH_ODDS");
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [expandedMarket, setExpandedMarket] = useState(null);
  const [bets, setBets] = useState([]);
  const [betsLoading, setBetsLoading] = useState(false);

  const [confirmModal, setConfirmModal] = useState(null);

  const fetchMarkets = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getSettledMarkets({ limit: LIMIT, offset: 0, search });
      if (resp?.success && Array.isArray(resp.data)) {
        setMarkets(resp.data);
      } else {
        setMarkets([]);
      }
    } catch {
      setMarkets([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchMarkets();
  }, [fetchMarkets]);

  const filteredMarkets = markets.filter(mkt => getTabForMarket(mkt) === activeTab);

  const tabCounts = {};
  TABS.forEach(t => { tabCounts[t.key] = 0; });
  markets.forEach(mkt => { tabCounts[getTabForMarket(mkt)]++; });

  const fetchBets = async (matchId, marketType) => {
    setBetsLoading(true);
    try {
      const resp = await getSettledBets({ match_id: matchId, market_type: marketType });
      if (resp?.success && Array.isArray(resp.data)) {
        setBets(resp.data);
      } else {
        setBets([]);
      }
    } catch {
      setBets([]);
    } finally {
      setBetsLoading(false);
    }
  };

  const handleExpandMarket = (market) => {
    if (expandedMarket?.matchId === market.matchId && expandedMarket?.marketType === market.marketType) {
      setExpandedMarket(null);
      setBets([]);
    } else {
      setExpandedMarket({ matchId: market.matchId, marketType: market.marketType });
      fetchBets(market.matchId, market.marketType);
    }
  };

  const handleVoidMarket = (market) => {
    setConfirmModal({ type: "market", data: market, loading: false });
  };

  const handleVoidBet = (bet) => {
    setConfirmModal({ type: "bet", data: bet, loading: false });
  };

  const confirmVoid = async () => {
    if (!confirmModal) return;
    setConfirmModal(prev => ({ ...prev, loading: true }));
    try {
      if (confirmModal.type === "market") {
        const resp = await voidMarketAfterSettlement({
          match_id: confirmModal.data.matchId,
          market_type: confirmModal.data.marketType,
        });
        if (resp?.success) {
          alert("Market voided successfully. Affected users: " + (resp.affectedUsers || 0));
        } else {
          alert("Failed: " + (resp?.error || "Unknown error"));
        }
      } else {
        const resp = await voidBetAfterSettlement({
          ledger_id: confirmModal.data.ledgerId,
        });
        if (resp?.success) {
          alert("Bet voided successfully for user: " + (confirmModal.data.username || confirmModal.data.userId));
        } else {
          alert("Failed: " + (resp?.error || "Unknown error"));
        }
      }
    } catch (err) {
      alert("Error: " + (err?.response?.data?.error || err.message));
    } finally {
      setConfirmModal(null);
      fetchMarkets();
      if (expandedMarket) {
        fetchBets(expandedMarket.matchId, expandedMarket.marketType);
      }
    }
  };

  const isExpanded = (mkt) =>
    expandedMarket?.matchId === mkt.matchId && expandedMarket?.marketType === mkt.marketType;

  return (
    <Layout>
      <div className="p-4 w-full mx-auto bg-[#171b36] min-h-screen">

        {/* ── Header ── */}
        <div style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }} className="shadow-sm px-4 pt-4">
          <div className="flex justify-between items-center mb-1">
            <div style={{ color: "#cae0e8", fontSize: "24px" }} className="font-semibold">
              Void Bets (After Settlement)
            </div>
            <button
              type="button"
              onClick={fetchMarkets}
              disabled={loading}
              className="px-4 py-2 bg-[#11374a] text-white text-[14px] font-bold hover:bg-[#1a4f66] transition-colors disabled:opacity-50"
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
          <div className="w-full h-px bg-white mb-2" />
        </div>

        {/* ── Search + Tabs ── */}
        <div style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-8px" }} className="shadow-sm px-4 pt-4 pb-2">
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              placeholder="Search by match ID, market type, team name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") fetchMarkets(); }}
              className="flex-1 px-3 py-2 bg-[#252d5c] text-white text-sm border border-[#333d79] rounded-none outline-none placeholder-gray-500 focus:border-[#4e5aa7]"
            />
            <button
              onClick={fetchMarkets}
              className="px-5 py-2 bg-[#11374a] text-white text-[12px] font-bold hover:bg-[#1a4f66] transition-colors"
            >
              Search
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setExpandedMarket(null); setBets([]); }}
                className={`px-4 py-2 text-[12px] font-bold transition-colors ${
                  activeTab === tab.key ? "bg-[#333d79] text-white" : "bg-gray-600 text-white"
                }`}
              >
                {tab.label} ({tabCounts[tab.key] || 0})
              </button>
            ))}
          </div>
        </div>

        {/* ── Markets Table ── */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-8px", minHeight: "500px" }}
          className="shadow-sm p-4"
        >
          <div className="flex-1 min-w-0">
            <div className="overflow-hidden">
              <table className="w-full text-sm table-fixed">
                <thead>
                  <tr className="border-b border-[#333d79]">
                    <th className="text-left px-4 py-3 text-white font-bold text-[15px] w-[35%]">Match Title</th>
                    <th className="text-left px-4 py-3 text-white font-bold text-[15px] w-[15%]">Market Type</th>
                    <th className="text-right px-4 py-3 text-white font-semibold text-[15px] w-[10%]">Entries</th>
                    <th className="text-right px-4 py-3 text-white font-semibold text-[15px] w-[10%]">Users</th>
                    <th className="text-right px-4 py-3 text-white font-semibold text-[15px] w-[12%]">Net Amount</th>
                    <th className="text-center px-4 py-3 text-white font-bold text-[15px] w-[18%]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="text-center text-gray-400 py-8 text-sm">Loading...</td></tr>
                  ) : filteredMarkets.length === 0 ? (
                    <tr><td colSpan={6} className="text-center text-gray-400 py-8 text-sm">No settled markets found</td></tr>
                  ) : (
                    filteredMarkets.map((mkt, idx) => (
                      <React.Fragment key={`${mkt.matchId}-${mkt.marketType}-${idx}`}>
                        <tr className={`transition-colors ${idx % 2 === 0 ? "bg-[rgba(255,255,255,0.04)]" : ""}`}>
                          <td className="py-3 px-4">
                            <div className="text-[#2ce5dd] font-medium truncate">
                              {mkt.matchTitle || (mkt.teamOne && mkt.teamTwo ? `${mkt.teamOne} vs ${mkt.teamTwo}` : mkt.matchId)}
                            </div>
                            <div className="text-gray-500 text-[11px] mt-0.5">{mkt.matchId}</div>
                          </td>
                          <td className="py-3 px-4 text-white font-bold truncate">{mkt.marketType || "-"}</td>
                          <td className="py-3 px-4 text-right text-white font-bold">{mkt.totalEntries}</td>
                          <td className="py-3 px-4 text-right text-white font-bold">{mkt.totalUsers}</td>
                          <td className="py-3 px-4 text-right font-bold">
                            <span className={mkt.totalNetAmount >= 0 ? "text-[#3cd247]" : "text-[#ef4444]"}>
                              {mkt.totalNetAmount >= 0 ? "+" : ""}{mkt.totalNetAmount?.toFixed(2)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleExpandMarket(mkt)}
                                className="px-3 py-1.5 bg-[#11374a] text-white text-[12px] font-bold hover:bg-[#1a4f66] transition-colors"
                              >
                                {isExpanded(mkt) ? "Hide Bets" : "View Bets"}
                              </button>
                              <button
                                onClick={() => handleVoidMarket(mkt)}
                                className="px-3 py-1.5 bg-[#b91c1c] text-white text-[12px] font-bold hover:bg-[#dc2626] transition-colors"
                              >
                                Void Market
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* ── Expanded: Individual Bets ── */}
                        {isExpanded(mkt) && (
                          <tr>
                            <td colSpan={6} className="px-4 pb-3 pt-0">
                              <div className="bg-[#0d1025] border border-[#252d5c] p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-[#cae0e8] text-[14px] font-bold">
                                    Individual Bets &mdash; {mkt.marketType}
                                  </span>
                                  <span className="text-gray-500 text-[12px]">
                                    {bets.length} bet{bets.length !== 1 ? "s" : ""}
                                  </span>
                                </div>

                                {betsLoading ? (
                                  <div className="text-gray-400 text-center py-6 text-sm">Loading bets...</div>
                                ) : bets.length === 0 ? (
                                  <div className="text-gray-400 text-center py-6 text-sm">No bets found for this market</div>
                                ) : (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="border-b border-[#252d5c]">
                                          <th className="text-left px-3 py-2 text-gray-400 font-semibold text-[12px]">Bet ID</th>
                                          <th className="text-left px-3 py-2 text-gray-400 font-semibold text-[12px]">User</th>
                                          <th className="text-left px-3 py-2 text-gray-400 font-semibold text-[12px]">Selection</th>
                                          <th className="text-center px-3 py-2 text-gray-400 font-semibold text-[12px]">Bet Type</th>
                                          <th className="text-right px-3 py-2 text-gray-400 font-semibold text-[12px]">Odds</th>
                                          <th className="text-right px-3 py-2 text-gray-400 font-semibold text-[12px]">Stake</th>
                                          <th className="text-right px-3 py-2 text-gray-400 font-semibold text-[12px]">Net Amount</th>
                                          <th className="text-center px-3 py-2 text-gray-400 font-semibold text-[12px]">Result</th>
                                          <th className="text-center px-3 py-2 text-gray-400 font-semibold text-[12px]">Action</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {bets.map((bet, bIdx) => (
                                          <tr
                                            key={bet.ledgerId}
                                            className={`border-b border-[#1a1f3d] ${bIdx % 2 === 0 ? "bg-[rgba(255,255,255,0.02)]" : ""}`}
                                          >
                                            <td className="px-3 py-2.5 text-[#2ce5dd] font-medium">#{bet.betId}</td>
                                            <td className="px-3 py-2.5 text-white font-medium">{bet.username}</td>
                                            <td className="px-3 py-2.5 text-white">{bet.selectionName || "-"}</td>
                                            <td className="px-3 py-2.5 text-center">
                                              {bet.betType ? (
                                                <span className={`inline-block min-w-[40px] px-2 py-0.5 text-[11px] font-bold text-center text-white ${
                                                  bet.betType === "back" || bet.betType === "yes" || bet.betType === "Yes"
                                                    ? "bg-[#3b82f6]"
                                                    : "bg-[#ec4899]"
                                                }`}>
                                                  {bet.betType.toUpperCase()}
                                                </span>
                                              ) : (
                                                <span className="text-gray-500">-</span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2.5 text-right text-white">{bet.odds ?? "-"}</td>
                                            <td className="px-3 py-2.5 text-right text-white">{bet.stakeAmount != null ? bet.stakeAmount.toFixed(2) : "-"}</td>
                                            <td className="px-3 py-2.5 text-right font-bold">
                                              <span className={bet.netamount >= 0 ? "text-[#3cd247]" : "text-[#ef4444]"}>
                                                {bet.netamount >= 0 ? "+" : ""}{bet.netamount?.toFixed(2)}
                                              </span>
                                            </td>
                                            <td className="px-3 py-2.5 text-center">
                                              {bet.resultStatus ? (
                                                <span className={`inline-block min-w-[40px] px-2 py-0.5 text-[11px] font-bold text-center text-white ${
                                                  bet.resultStatus === "won" ? "bg-[#16a34a]" : bet.resultStatus === "loss" ? "bg-[#dc2626]" : "bg-gray-600"
                                                }`}>
                                                  {bet.resultStatus.toUpperCase()}
                                                </span>
                                              ) : (
                                                <span className="text-gray-500">-</span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2.5 text-center">
                                              <button
                                                onClick={() => handleVoidBet(bet)}
                                                className="px-3 py-1.5 bg-[#c2410c] text-white text-[12px] font-bold hover:bg-[#ea580c] transition-colors"
                                              >
                                                Void Bet
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Confirmation Modal ── */}
      <Dialog.Root open={!!confirmModal} onOpenChange={(open) => { if (!open) setConfirmModal(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60" style={{ zIndex: 9999 }} />
          <Dialog.Content
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#222951] border border-[#333d79] p-6 shadow-xl w-[calc(100vw-2rem)] max-w-[480px]"
            style={{ zIndex: 10000 }}
          >
            <Dialog.Title className="text-white text-lg font-bold mb-4">
              {confirmModal?.type === "market" ? "Void Entire Market" : "Void Single Bet"}
            </Dialog.Title>

            <div className="border-l-4 border-[#f59e0b] bg-[rgba(245,158,11,0.08)] p-4 mb-5">
              {confirmModal?.type === "market" ? (
                <div className="text-white text-sm leading-relaxed">
                  <p>
                    Are you sure you want to void <strong>all bets</strong> in market{" "}
                    <span className="text-[#2ce5dd]">{confirmModal?.data?.marketType}</span> for match{" "}
                    <span className="text-[#2ce5dd]">{confirmModal?.data?.matchTitle || confirmModal?.data?.matchId}</span>?
                  </p>
                  <p className="mt-3 text-gray-300">
                    This will reverse settled amounts for{" "}
                    <strong className="text-white">{confirmModal?.data?.totalEntries} entries</strong> across{" "}
                    <strong className="text-white">{confirmModal?.data?.totalUsers} users</strong>.
                    This action cannot be undone.
                  </p>
                </div>
              ) : (
                <div className="text-white text-sm leading-relaxed">
                  <p>
                    Are you sure you want to void bet{" "}
                    <strong>#{confirmModal?.data?.betId}</strong> for user{" "}
                    <span className="text-[#2ce5dd]">{confirmModal?.data?.username}</span>?
                  </p>
                  <p className="mt-3 text-gray-300">
                    Net amount to reverse:{" "}
                    <strong className={confirmModal?.data?.netamount >= 0 ? "text-[#3cd247]" : "text-[#ef4444]"}>
                      {confirmModal?.data?.netamount >= 0 ? "+" : ""}{confirmModal?.data?.netamount?.toFixed(2)}
                    </strong>.
                    This action cannot be undone.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <Dialog.Close asChild>
                <button className="px-4 py-2 bg-gray-600 text-white text-sm font-bold hover:bg-gray-500 transition-colors">
                  Cancel
                </button>
              </Dialog.Close>
              <button
                onClick={confirmVoid}
                disabled={confirmModal?.loading}
                className="px-4 py-2 bg-[#b91c1c] text-white text-sm font-bold hover:bg-[#dc2626] transition-colors disabled:opacity-50"
              >
                {confirmModal?.loading ? "Processing..." : "Confirm Void"}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Layout>
  );
}
